import MapboxContext from '@sofarocean/mapbox-context/dist/contexts/MapboxContext';
import { useContext, useEffect } from 'react';

import { Device } from '@/api/devices';
import { useGeofenceDisplayData } from '@/hooks/useGeofenceDisplayData';

import {
    GEOFENCE_LINE_DASHARRAY,
    GEOFENCE_LINE_WIDTH,
    addOrUpdateGeoJSONLayer,
    createCircleGeometry,
    getGeofenceColors,
    removeGeoJSONLayer,
} from './geofenceUtils';

export type GeofenceDisplayLayerProps = {
    device?: Device;
    layerId: string;
    beforeLayer?: string;
};

export const GeofenceDisplayLayer = ({ device, layerId, beforeLayer }: GeofenceDisplayLayerProps) => {
    const { map } = useContext(MapboxContext);
    const deviceGeofence = useGeofenceDisplayData(device);

    const fillId = `geofence-display-fill-${layerId}`;
    const lineId = `geofence-display-line-${layerId}`;

    useEffect(() => {
        if (!map || !deviceGeofence) {
            return;
        }

        const circle = createCircleGeometry(deviceGeofence.center, deviceGeofence.radius);
        const { fillColor, lineColor, fillOpacity } = getGeofenceColors(deviceGeofence.isBreached);

        // Add circle fill layer
        addOrUpdateGeoJSONLayer(
            map,
            { sourceId: fillId, beforeId: beforeLayer },
            {
                id: fillId,
                type: 'fill',
                source: fillId,
                layout: {},
                paint: {
                    'fill-color': fillColor,
                    'fill-opacity': fillOpacity,
                },
            },
            circle
        );

        // Add circle outline layer
        addOrUpdateGeoJSONLayer(
            map,
            { sourceId: lineId, beforeId: beforeLayer },
            {
                id: lineId,
                type: 'line',
                source: lineId,
                layout: {},
                paint: {
                    'line-color': lineColor,
                    'line-width': GEOFENCE_LINE_WIDTH,
                    'line-dasharray': GEOFENCE_LINE_DASHARRAY,
                },
            },
            circle
        );

        return () => {
            removeGeoJSONLayer(map, fillId, fillId);
            removeGeoJSONLayer(map, lineId, lineId);
        };
    }, [map, deviceGeofence, fillId, lineId, beforeLayer]);

    return null;
};
